import React from "react";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { CvEditorSession } from "../types/cv-editor.types";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";

interface CompletionStepProps {
  session: CvEditorSession;
  onClose: () => void;
}

export const CompletionStep: React.FC<CompletionStepProps> = ({
  session,
  onClose,
}) => {
  const { t } = useTranslation("cvEditor");

  // This helper function handles the file download logic
  const handleDownload = () => {
    if (!session?.finalCvJson) {
      console.error("No final JSON data available to download.");
      return;
    }

    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(session.finalCvJson, null, 2)
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    // Create a filename like 'cv-data-2023-10-27.json'
    const filename = `cv-data-${new Date().toISOString().split("T")[0]}.json`;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="flex-grow flex flex-col items-center mt-10 text-center">
        <h2 className="mt-4 text-xl font-bold text-gray-900 mb-8">
          {t("completionStep.title")}
        </h2>
        <CheckCircleOutlineIcon
          className="text-Yellow-default mb-8"
          sx={{ fontSize: 100 }}
        />
        <p className="mt-2 max-w-md text-gray-600">
          {t("completionStep.description")}
        </p>
      </div>

      <div className="flex-shrink-0 mt-auto pt-6 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <Button
            type="button"
            className="w-full sm:w-auto"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleDownload}
            disabled={!session?.finalCvJson}
          >
            <DownloadIcon sx={{ fontSize: 20, marginRight: 1 }} />
            {t("completionStep.downloadButton")}
          </Button>
          <Button
            className="w-full sm:w-auto"
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={onClose}
          >
            {t("common.close")}
          </Button>
        </div>
      </div>
    </div>
  );
};
